import { useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { theme, typography, spacing, borderRadius, mobileSpacing, isMobile } from '../styles/theme';
import { Sentry } from '../sentry';

export default function RuleLink({ ruleNumber }) {
  const [ruleText, setRuleText] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Fetch rule text from backend
  const fetchRuleText = async () => {
    if (ruleText) return; // Already loaded

    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/rules/${encodeURIComponent(ruleNumber)}`
      );
      setRuleText(response.data.text);
    } catch (error) {
      console.error('Error fetching rule:', error);
      Sentry.captureException(error, {
        tags: {
          component: 'RuleLink',
          ruleNumber: ruleNumber,
        },
        level: 'warning',
      });
      setRuleText('Rule text not found');
    } finally {
      setLoading(false);
    }
  };

  // Desktop: Hover to preview
  const handleMouseEnter = async (e) => {
    if (isMobile()) return;
    
    setShowPreview(true);
    setMousePos({ x: e.clientX, y: e.clientY });
    await fetchRuleText();
  };

  const handleMouseMove = (e) => {
    if (isMobile()) return;
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseLeave = () => {
    if (isMobile()) return;
    setShowPreview(false);
  };

  // Mobile: Click to show modal
  const handleClick = async (e) => {
    if (!isMobile()) return;
    
    e.preventDefault();
    await fetchRuleText();
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Desktop hover preview
  const preview = showPreview && ruleText && !isMobile() && (
    <div style={{
      position: 'fixed',
      left: mousePos.x + 15,
      top: mousePos.y + 15,
      zIndex: 9999,
      pointerEvents: 'none',
      backgroundColor: theme.bg.primary,
      border: `1px solid ${theme.border.light}`,
      borderRadius: borderRadius.md,
      padding: spacing.md,
      maxWidth: '400px',
      boxShadow: theme.shadow.large,
    }}>
      <div style={{
        fontSize: typography.fontSize.xs,
        fontWeight: typography.fontWeight.semibold,
        color: theme.brand.primary,
        marginBottom: spacing.xs,
      }}>
        Rule {ruleNumber}
      </div>
      <div style={{
        fontSize: typography.fontSize.sm,
        color: theme.text.primary,
        lineHeight: typography.lineHeight.relaxed,
      }}>
        {ruleText}
      </div>
    </div>
  );

  // Mobile modal
  const modal = showModal && (
    <div
      onClick={handleCloseModal}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: mobileSpacing.lg,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: theme.bg.primary,
          borderRadius: borderRadius.lg,
          padding: mobileSpacing.lg,
          maxWidth: '90%',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: theme.shadow.xlarge,
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: mobileSpacing.md,
          borderBottom: `1px solid ${theme.border.light}`,
          paddingBottom: mobileSpacing.sm,
        }}>
          <h3 style={{
            margin: 0,
            fontSize: typography.fontSize.lg,
            fontWeight: typography.fontWeight.semibold,
            color: theme.brand.primary,
          }}>
            Rule {ruleNumber}
          </h3>
          <button
            onClick={handleCloseModal}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: theme.text.primary,
              padding: mobileSpacing.sm,
              minWidth: '44px',
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ×
          </button>
        </div>

        {/* Rule text */}
        {loading ? (
          <div style={{
            padding: mobileSpacing.xl,
            color: theme.text.secondary,
            textAlign: 'center',
          }}>
            Loading...
          </div>
        ) : (
          <div style={{
            fontSize: typography.fontSize.base,
            color: theme.text.primary,
            lineHeight: typography.lineHeight.relaxed,
          }}>
            {ruleText}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <span
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{
          color: theme.brand.primary,
          textDecoration: 'underline',
          textDecorationStyle: 'dotted',
          cursor: 'pointer',
          fontWeight: typography.fontWeight.medium,
        }}
      >
        {ruleNumber}
      </span>

      {/* Desktop: Hover preview */}
      {showPreview && createPortal(preview, document.body)}

      {/* Mobile: Modal */}
      {showModal && createPortal(modal, document.body)}
    </>
  );
}